import type { LogFields } from '#@/record.ts'

/**
 * The structural subset of `AsyncLocalStorage<LogFields>` the logger needs. A Worker entry registers a real
 * `AsyncLocalStorage` once; the package never imports `node:async_hooks` because browser and React Native bundles have
 * no such module.
 */
export type LogContextStorage = {
  getStore: () => LogFields | undefined
  run: <T>(store: LogFields, fn: () => T) => T
}

/**
 * Adds `fields` to every record emitted by work that starts inside `fn`. Nested calls merge shallowly and the inner
 * call wins. Without a registered storage `fn` runs once and no field is added. `fn` runs exactly once in every case;
 * its result and its exception propagate unchanged.
 */
export function withLogContext<T>(fields: LogFields, fn: () => T): T {
  if (storage === undefined) {
    return fn()
  }
  return storage.run({ ...readLogContext(), ...fields }, fn)
}

/** Registers the storage `withLogContext` runs in. A second call replaces the first; `undefined` removes it. */
export function setLogContextStorage(next: LogContextStorage | undefined): void {
  storage = next
}

/** The active context fields, or `undefined` outside a scope or when the storage throws. */
export function readLogContext(): LogFields | undefined {
  try {
    return storage?.getStore()
  } catch {
    return undefined
  }
}

let storage: LogContextStorage | undefined = undefined
